import {
  type EWarehouseOrderStatus,
  type IOrderWarehouseOrderSummary,
} from '@mahawi/eshop-common/src/types';
import { Divider, Space, Tag, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { warehouseOrdersLoadByOrder } from '../../reducers/warehouse-order/reducer';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import TableComponent from '../table';
import {
  formatAmount,
  formatEur,
  WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL,
} from './labels';
import ProfitText from './profit-text';
import { WarehouseOrderStatusTag } from './status-tag';

const { Text } = Typography;

type TWarehouseOrderRow =
  IOrderWarehouseOrderSummary['warehouseOrders'][number] & { key: string };

const columns: ColumnsType<TWarehouseOrderRow> = [
  {
    title: 'Warehouse order',
    render: (
      _value: string,
      record: TWarehouseOrderRow,
    ): React.ReactElement => (
      <Link
        to={`/orders/warehouse-order/${record.uuid}`}
        label={`${record.warehouse.name} ${record.code}`}
        openInNewTab
      />
    ),
  },
  {
    title: 'Ordered',
    dataIndex: 'orderedAt',
    render: (orderedAt: string): string =>
      dayjs(orderedAt).format(EDateFormat.DATE),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status: EWarehouseOrderStatus): React.ReactElement => (
      <WarehouseOrderStatusTag status={status} />
    ),
  },
  {
    title: 'Paired by',
    render: (_value: string, record: TWarehouseOrderRow): string =>
      WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL[record.source],
  },
  { title: 'Supplied pieces', dataIndex: 'coveredQuantity', align: 'right' },
  {
    title: 'Cost',
    align: 'right',
    render: (
      _value: string,
      record: TWarehouseOrderRow,
    ): React.ReactElement => (
      <Space orientation="vertical" size={SPACING.none}>
        <Text>
          {formatAmount(record.purchasePrice, record.currencyIsoCode)}
        </Text>
        <Text type="secondary">{formatEur(record.purchasePriceEur)}</Text>
      </Space>
    ),
  },
];

function OrderWarehouseOrders({
  orderUUID,
}: {
  orderUUID: string;
}): React.ReactElement | null {
  const dispatch = useAppDispatch();
  const summary = useSelector((s: RootState) => s.WarehouseOrder.byOrder);

  useEffect((): void => {
    dispatch(warehouseOrdersLoadByOrder(orderUUID));
  }, [dispatch, orderUUID]);

  if (!summary || summary.warehouseOrders.length === 0) {
    return null;
  }

  const isCostComplete: boolean =
    summary.coveredQuantity >= summary.orderedQuantity;

  return (
    <>
      <Divider titlePlacement="left">Warehouse orders</Divider>
      <Space orientation="vertical" size={SPACING.md} style={{ width: '100%' }}>
        <Space size={SPACING.xl} wrap>
          <Text>
            <Text type="secondary">Revenue without VAT:</Text>{' '}
            {formatEur(summary.revenueEur)}
          </Text>
          <Text>
            <Text type="secondary">Purchase cost:</Text>{' '}
            {formatEur(summary.purchasePriceEur)}
          </Text>
          <Text>
            <Text type="secondary">Profit:</Text>{' '}
            <ProfitText value={summary.profitEur} />
          </Text>
          {!isCostComplete && (
            <Tag color="orange">
              Purchase price known for {summary.coveredQuantity} of{' '}
              {summary.orderedQuantity} pieces
            </Tag>
          )}
        </Space>
        <TableComponent
          columns={columns as Parameters<typeof TableComponent>[0]['columns']}
          dataSource={summary.warehouseOrders.map((warehouseOrder) => ({
            key: warehouseOrder.uuid,
            ...warehouseOrder,
          }))}
          isLoading={false}
          pagination={false}
          size="small"
        />
      </Space>
    </>
  );
}

export default OrderWarehouseOrders;
